import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardFooterComponent } from '../../../shared/card-footer/card-footer.component';
import { HeaderComponent } from '../../../shared/header/header.component';
import { PasswordInputComponent } from '../../../shared/password-input/password-input.component';
import { EmployerAuthService } from '../../../shared/services/employer-auth.service';
import { ErrorMessageService } from '../../../shared/services/error-message.service';


@Component({
  selector: 'app-signup',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HeaderComponent,
    CardFooterComponent,
    TranslateModule,
    PasswordInputComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.css',
})
export class SignupComponent implements OnInit {
  policyNumber = '';
  employerEmail = '';
  password = '';
  confirmPassword = '';

  policyNumberErrors: string[] = [];
  employerEmailErrors: string[] = [];
  passwordErrors: string[] = [];
  confirmPasswordErrors: string[] = [];

  policyNumberTouched = false;
  employerEmailTouched = false;
  passwordTouched = false;
  confirmPasswordTouched = false;

  passwordStrength = '';
  passwordStrengthText = '';
  isLoading = false;
  errorMessage = '';

  constructor(
    private titleService: Title,
    private router: Router,
    private translate: TranslateService,
    private authService: EmployerAuthService,
    private errorMessageService: ErrorMessageService
  ) { }

  ngOnInit(): void {
    this.titleService.setTitle('Employer | Sign Up');
    this.translate.use(this.translate.currentLang || 'en');
    this.errorMessage = '';
  }

  validatePolicyNumber() {
    this.policyNumberErrors = [];

    if (!this.policyNumber.trim()) {
      this.policyNumberErrors.push('EMPLOYER.SIGNUP.POLICY_NUMBER_REQUIRED');
    }
  }

  validateEmployerEmail() {
    this.employerEmailErrors = [];

    if (!this.employerEmail.trim()) {
      this.employerEmailErrors.push('EMPLOYER.SIGNUP.EMAIL_REQUIRED');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.employerEmail)) {
        this.employerEmailErrors.push('EMPLOYER.SIGNUP.EMAIL_INVALID');
      }
    }
  }

  validatePassword() {
    this.passwordErrors = [];

    if (!this.password) {
      this.passwordErrors.push('EMPLOYER.SIGNUP.PASSWORD_REQUIRED');
      this.passwordStrength = 'weak';
      this.passwordStrengthText = '';
      return;
    }

    const hasUpperCase = /[A-Z]/.test(this.password);
    const hasLowerCase = /[a-z]/.test(this.password);
    const hasNumbers = /\d/.test(this.password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.password);
    const hasMinLength = this.password.length >= 8;

    const requirementsMet = [
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
      hasMinLength,
    ].filter(Boolean).length;

    if (this.password.length >= 8 && requirementsMet >= 4) {
      if (requirementsMet === 5) {
        this.passwordStrength = 'strong';
        this.passwordStrengthText = 'Strong password';
      } else {
        this.passwordStrength = 'medium';
        this.passwordStrengthText = 'Medium strength password';
      }
    } else {
      this.passwordErrors.push('EMPLOYER.SIGNUP.PASSWORD_INVALID');
      this.passwordStrength = 'weak';
      this.passwordStrengthText = 'Weak password';
    }

    if (this.confirmPassword) {
      this.validateConfirmPassword();
    }
  }

  validateConfirmPassword() {
    this.confirmPasswordErrors = [];

    if (!this.confirmPassword) {
      this.confirmPasswordErrors.push('EMPLOYER.SIGNUP.CONFIRM_PASSWORD_REQUIRED');
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.confirmPasswordErrors.push('EMPLOYER.SIGNUP.PASSWORDS_MISMATCH');
    }
  }

  isFormValid(): boolean {
    this.validatePolicyNumber();
    this.validateEmployerEmail();
    this.validatePassword();
    this.validateConfirmPassword();

    return (
      this.policyNumberErrors.length === 0 &&
      this.employerEmailErrors.length === 0 &&
      this.passwordErrors.length === 0 &&
      this.confirmPasswordErrors.length === 0 &&
      !!this.policyNumber.trim() &&
      !!this.employerEmail.trim() &&
      !!this.password &&
      !!this.confirmPassword
    );
  }

  submitOnEnter() {
    if (!this.isLoading && this.isFormValid()) {
      this.register();
    }
  }

  register() {
    this.policyNumberTouched = true;
    this.employerEmailTouched = true;
    this.passwordTouched = true;
    this.confirmPasswordTouched = true;
    this.errorMessage = '';

    if (!this.isFormValid()) {
      return;
    }

    this.isLoading = true;
    const signupData = {
      policyNumber: this.policyNumber,
      email: this.employerEmail,
      password: this.password,
      confirmPassword: this.confirmPassword,
      employType: 'employer'
    };

    // Call validate API (assume EmployerAuthService has validateSignup)
    this.authService.validateSignup(signupData).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (this.errorMessageService.isSuccessResponse(response)) {
          // Store signupData in sessionStorage and navigate to agreement page
          sessionStorage.setItem('agreementSignupData', JSON.stringify(signupData));
          this.router.navigate(['/employer/account/agreement']);
        } else {
          this.errorMessageService.getErrorMessage(response.errorCode).subscribe(message => {
            this.errorMessage = message;
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        if (error.error && error.error.errorCode) {
          this.errorMessageService.getErrorMessage(error.error.errorCode).subscribe(message => {
            this.errorMessage = message;
          });
        } else {
          this.translate.get('error.unknownError').subscribe(message => {
            this.errorMessage = error.error?.message || message;
          });
        }
      }
    });
  }
} 